// Task row — inline-edit title, status dropdown, assignee picker, dates.
// Click anywhere except the editable cells to open the TaskModal.

import { AlertCircle, Calendar, Check, ChevronDown, Diamond, Flag, GripVertical, Paperclip } from "lucide-preact";
import type { ApiWorkItemType, LabelItem, Status, StatusInfo, Task, TaskPriority, TaskStatus } from "@juuplan/shared";
import {
  formatDateShort,
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
  TASK_STATUS_LABEL,
  TASK_STATUS_VAR,
} from "../lib/format";
import {
  GRID_GAP_CLASS,
  GRID_PADDING_X_PX,
  ROW_BG_BASE,
  ROW_BG_SELECTED,
  ROW_BG_VAR,
  frozenMinWidthPx,
  gridMinWidth,
  gridSpacingClass,
  splitGridTemplate,
  type TableColumnId,
} from "../lib/columns";
import { AssigneePicker, AvatarStack } from "./AssigneePicker";
import { TaskDateCell } from "./TaskDateCell";
import { LabelChip, pickFg } from "./LabelChip";
import { WorkItemTypeBadge } from "./WorkItemTypeBadge";
import { AiOriginBadge } from "./AiOriginBadge";
import { UserAvatar } from "./UserAvatar";
import { RowActions } from "./RowActions";
import { findUser } from "../store/users";

interface Props {
  task: Task;
  onOpen: () => void;
  /** Title is edited inline only inside TaskModal — kept on the props for
   *  symmetry with future kanban/calendar callbacks. */
  onTitle?: (next: string) => void | Promise<void>;
  onStatus: (next: TaskStatus) => void | Promise<void>;
  /** Called with the custom status id when the project uses dynamic statuses. */
  onStatusId?: (id: number) => void | Promise<void>;
  onAssignees: (next: number[]) => void | Promise<void>;
  onStartDate: (next: string | null) => void | Promise<void>;
  onEndDate: (next: string | null) => void | Promise<void>;
  onPriority: (next: TaskPriority | null) => void | Promise<void>;
  /** Delete the task — when provided (and not readOnly), a trash button
   *  appears in the row's hover-action cluster on the right. */
  onDelete?: () => void | Promise<void>;
  /** UX-Vorschlag P6: Zeilenaktion „Sub-Aufgabe anlegen" (nur mit Flag). */
  onAddSubtask?: () => void;
  /** Owning project id — passed through to AssigneePicker so it can default
   *  to "project members" instead of every active user (#875). Optional so
   *  rows rendered outside a project context keep today's "all users"
   *  behavior untouched. */
  projectId?: number;
  dragHandleProps?: Record<string, unknown>;
  /** Columns the user has hidden via the toolbar; cells with these ids are not rendered. */
  hiddenColumns?: Set<TableColumnId>;
  /** Project label cache (id → LabelItem) — used to resolve label_ids on rows. */
  labelsById?: Map<number, LabelItem>;
  /** Project work-item-type cache (id → type) — resolves task.type_id for the Typ column. */
  typesById?: Map<number, ApiWorkItemType>;
  /** Project's custom statuses (sorted by sortOrder). When provided and the task
   *  has statusInfo, the status cell uses these instead of the legacy enum. */
  statuses?: Status[];
  /** Viewer-Rolle: inline-Edits gesperrt. */
  readOnly?: boolean;
  /** Bulk-select: whether this row is currently selected. */
  selected?: boolean;
  /** Bulk-select: callback to toggle selection for this row. */
  onSelect?: (id: number) => void;
  /** Bulk-select: whether any row in the list is currently selected. */
  anySelected?: boolean;
}

export function TaskRowKompakt(props: Props): preact.JSX.Element {
  const {
    task,
    onOpen,
    onStatus,
    onStatusId,
    onAssignees,
    onStartDate,
    onEndDate,
    onPriority,
    onDelete,
    projectId,
    dragHandleProps,
    hiddenColumns,
    labelsById,
    typesById,
    statuses,
    readOnly = false,
    selected = false,
    onSelect,
    anySelected = false,
  } = props;

  const hidden = hiddenColumns ?? new Set<TableColumnId>();
  const { frozen: frozenTemplate, scrolling: scrollingTemplate } =
    splitGridTemplate(hidden, "kompakt");

  const workItemType =
    task.type_id != null ? (typesById?.get(task.type_id) ?? null) : null;

  const labels: LabelItem[] = labelsById
    ? task.label_ids
        .map((id) => labelsById.get(id))
        .filter((l): l is LabelItem => l !== undefined)
    : [];
  const visibleLabels = labels.slice(0, 2);
  const overflowCount = labels.length - visibleLabels.length;

  return (
    <>
      <MobileTaskCardKompakt
        task={task}
        labels={labels}
        onOpen={onOpen}
        onToggleDone={
          readOnly
            ? undefined
            : () => void onStatus(task.status === "done" ? "in_progress" : "done")
        }
      />
    <div
      class={`juuplan-row hidden md:flex group ${gridSpacingClass("kompakt")} items-center py-1.5 text-sm border-b border-[color:var(--color-outline-variant)]/60 bg-[color:var(--juuplan-row-bg)] hover:bg-[color:var(--color-surface-container-low)] hover:shadow-[inset_3px_0_0_var(--color-primary)] transition-all duration-100`}
      style={{
        minWidth: gridMinWidth(hidden, "kompakt"),
        // Deckender Zeilen-Hintergrund als Variable — die hover:-Klasse bleibt
        // wirksam, und der eingefrorene Block erbt den Ton.
        [ROW_BG_VAR]: selected ? ROW_BG_SELECTED : ROW_BG_BASE,
      }}
      /* EINE GESTE ZUM ÖFFNEN: einfacher Klick auf den Titel (der Titel ist
         ein Button, siehe unten) — plus die Hover-Aktion „Bearbeiten" rechts.
         Der vorherige `onDblClick` auf der GANZEN Zeile war die zweite Geste
         für dieselbe Handlung: `RecordRow` öffnet mit einem Klick, diese
         Zeile brauchte zwei — auf demselben Bildschirm. Doppelklick ist
         zudem auf Touch unzuverlässig (er wird dort zu Zoom/Auswahl), und
         ein Klick auf die ganze Zeile geht hier nicht: die Zeile ist voll
         von Feldern, die selbst Klicks brauchen (Datum, Status, Priorität,
         Zuständige). */
    >
      {/* Eingefrorener Block: Griff, Typ-Marke, Titel — bleibt beim
          waagerechten Scrollen links stehen (sticky greift nur, weil das hier
          ein FLEX-Item ist, siehe lib/columns.ts). */}
      <div
        class={`sticky left-0 z-[1] grid grow items-center ${GRID_GAP_CLASS.kompakt} transition-colors duration-100`}
        style={{
          gridTemplateColumns: frozenTemplate,
          flexBasis: 0,
          minWidth: `${frozenMinWidthPx("kompakt")}px`,
          marginLeft: `-${GRID_PADDING_X_PX.kompakt}px`,
          paddingLeft: `${GRID_PADDING_X_PX.kompakt}px`,
          backgroundColor: "inherit",
        }}
      >
      {/* Drag handle / selection checkbox */}
      {onSelect ? (
        <span class="relative flex items-center justify-center">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => { e.stopPropagation(); onSelect(task.id); }}
            onClick={(e) => e.stopPropagation()}
            class={`size-3.5 accent-[color:var(--color-primary)] cursor-pointer transition-opacity ${selected || anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
            aria-label={`Aufgabe „${task.title}" auswählen`}
          />
        </span>
      ) : readOnly ? (
        <span aria-hidden="true" />
      ) : (
        <span
          {...(dragHandleProps ?? {})}
          class="cursor-grab text-[color:var(--color-on-surface-variant)] opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Aufgabe verschieben"
          title="Aufgabe verschieben"
        >
          <GripVertical size={14} aria-hidden="true" />
        </span>
      )}

      {/* Type / milestone marker */}
      <span class="flex items-center justify-center" aria-hidden="true">
        {task.is_milestone ? (
          <Diamond
            size={14}
            class="text-[color:var(--color-prio-urgent)]"
            fill="currentColor"
          />
        ) : (
          <span
            class="inline-block size-2.5 rounded-sm border"
            style={{
              borderColor: TASK_STATUS_VAR[task.status],
              background:
                task.status === "done" ? TASK_STATUS_VAR.done : "transparent",
            }}
          />
        )}
      </span>

      {/* Title */}
      <button
        type="button"
        onClick={onOpen}
        class="inline-flex items-center gap-1.5 text-left text-sm text-[color:var(--color-on-surface)] truncate rounded px-1 -mx-1 hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[28px]"
        title={
          !task.is_milestone && task.status === "blocked"
            ? `${task.title} (blockiert)`
            : task.title
        }
      >
        {!task.is_milestone && task.status === "blocked" && (
          <AlertCircle
            size={14}
            class="text-[color:var(--color-status-blocked)] shrink-0"
            aria-label="Blockiert"
          />
        )}
        <span class={`truncate ${task.status === "done" ? "line-through opacity-70" : ""}`}>
          {task.title}
        </span>
        <AiOriginBadge origin={task.ai_origin} />
        {/* Attachment-count badge — only when at least one file is attached. */}
        {task.attachment_count > 0 && (
          <span
            class="shrink-0 inline-flex items-center gap-0.5 text-label text-[color:var(--color-on-surface-variant)]"
            title={`${task.attachment_count} Anhang${task.attachment_count === 1 ? "" : "änge"}`}
            aria-label={`${task.attachment_count} Anhänge`}
          >
            <Paperclip size={14} aria-hidden="true" />
            <span class="leading-none">{task.attachment_count}</span>
          </span>
        )}
        {/* When the Status column is hidden, surface a tiny status pill
            next to the title so the row keeps its at-a-glance state. */}
        {hidden.has("status") && (
          <span
            class="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-accent)]"
            style={{ background: TASK_STATUS_VAR[task.status] }}
            title={`Status: ${TASK_STATUS_LABEL[task.status]}`}
          >
            {TASK_STATUS_LABEL[task.status]}
          </span>
        )}
      </button>
      </div>

      {/* Mitscrollender Block: alle übrigen Spalten, `shrink-0` damit die
          festen Spurbreiten stehen bleiben statt gequetscht zu werden. */}
      <div
        class={`grid shrink-0 items-center ${GRID_GAP_CLASS.kompakt}`}
        style={{ gridTemplateColumns: scrollingTemplate }}
      >
      {/* Ticket number — dedicated, toggleable column. */}
      {!hidden.has("ticket") && (
        <span
          class="select-text truncate font-mono text-label font-medium text-[color:var(--color-on-surface-variant)]"
          title={task.ticket ?? undefined}
        >
          {task.ticket ?? "—"}
        </span>
      )}

      {/* Work-item type — colored pill from the project type cache */}
      {!hidden.has("type") && (
        <span class="inline-flex items-center min-w-0">
          <WorkItemTypeBadge type={workItemType} compact />
        </span>
      )}

      {/* Status */}
      {!hidden.has("status") && (
        <StatusSelect
          value={task.status}
          statusInfo={task.statusInfo ?? null}
          statuses={statuses}
          onChange={onStatus}
          onChangeId={onStatusId}
          readOnly={readOnly}
        />
      )}

      {/* Assignees — `emptyLabel="—"` aus demselben Grund wie in der
          Komfort-Zeile: „— Nicht zugewiesen —" umbrach dreizeilig. */}
      {!hidden.has("assignees") &&
        (readOnly ? (
          <span class="inline-flex items-center min-h-[28px]">
            <AvatarStack userIds={task.assignee_ids} />
          </span>
        ) : (
          <AssigneePicker
            value={task.assignee_ids}
            onChange={onAssignees}
            compact
            label="Zuständige"
            emptyLabel="—"
            projectId={projectId}
          />
        ))}

      {/* Labels — read-only display; editing lives in TaskModal. */}
      {!hidden.has("labels") && (
        <button
          type="button"
          onClick={onOpen}
          class="w-full inline-flex items-center gap-1 min-h-[28px] px-1 -mx-1 rounded text-left hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors overflow-hidden"
          aria-label={labels.length === 0 ? "Labels — keine" : `Labels: ${labels.map((l) => l.name).join(", ")}`}
          title={labels.length === 0 ? "Labels (im Modal bearbeiten)" : labels.map((l) => l.name).join(", ")}
        >
          {labels.length === 0 ? (
            <span class="text-label text-[color:var(--color-on-surface-variant)]/60">
              —
            </span>
          ) : (
            <>
              {visibleLabels.map((l) => (
                <LabelChip key={l.id} name={l.name} color={l.color} compact />
              ))}
              {overflowCount > 0 && (
                <span
                  class="shrink-0 inline-flex items-center rounded px-1 py-0 text-label font-medium leading-4 bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
                  title={labels
                    .slice(2)
                    .map((l) => l.name)
                    .join(", ")}
                >
                  +{overflowCount}
                </span>
              )}
            </>
          )}
        </button>
      )}

      {/* Start-/Enddatum — dieselbe Zelle wie in der Komfort-Zeile. Vorher
          trug die Kompakt-Zeile hier zwei dauerhaft umrandete Felder; das
          machte aus der Tabelle ein Kästchengitter, in dem 20 leere Kästchen
          „nichts" anzeigten. */}
      {!hidden.has("start") && (
        <TaskDateCell
          value={task.start_date}
          onChange={onStartDate}
          ariaLabel="Startdatum"
          readOnly={readOnly}
        />
      )}

      {!hidden.has("end") && (
        <TaskDateCell
          value={task.end_date}
          onChange={onEndDate}
          ariaLabel="Enddatum"
          readOnly={readOnly}
        />
      )}

      {/* Priority */}
      {!hidden.has("priority") && (
        <PrioritySelect value={task.priority ?? null} onChange={onPriority} readOnly={readOnly} />
      )}

      {/* Hover-Aktionen: Bearbeiten + Löschen */}
      {!readOnly && onDelete ? (
        <RowActions title={task.title} onEdit={onOpen} onDelete={onDelete} onAddSubtask={props.onAddSubtask} />
      ) : (
        <span aria-hidden="true" />
      )}
      </div>
    </div>
    </>
  );
}

// Compact card variant rendered on <768px. Inline edits live in the modal;
// the whole card just opens it. Keeps the at-a-glance signals (status color,
// title, due-date, assignees, labels) without the horizontal scroll-hell of
// a 7-column grid on a phone.
function MobileTaskCardKompakt(props: {
  task: Task;
  labels: LabelItem[];
  onOpen: () => void;
  onToggleDone?: () => void;
}): preact.JSX.Element {
  const { task, labels, onOpen, onToggleDone } = props;
  const assignees = task.assignee_ids
    .map((id) => findUser(id))
    .filter((u): u is NonNullable<ReturnType<typeof findUser>> => u !== null)
    .slice(0, 3);
  const extraAssignees = Math.max(0, task.assignee_ids.length - assignees.length);
  const due = task.end_date ?? task.start_date;
  const isDone = task.status === "done";
  const isBlocked = !task.is_milestone && task.status === "blocked";

  // Card is a div (not a button) so the done-toggle button can live inside it
  // without nesting <button> in <button>. Tap-to-open on the div; the toggle
  // stops propagation.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      class="md:hidden w-full text-left px-3 py-2.5 border-b border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-lowest)] active:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-inset transition-colors flex flex-col gap-1.5 cursor-pointer"
      style={{ minHeight: "56px" }}
    >
      <div class="flex items-center gap-2">
        {task.is_milestone ? (
          <Diamond
            size={14}
            class="text-[color:var(--color-prio-urgent)] shrink-0"
            fill="currentColor"
            aria-hidden="true"
          />
        ) : onToggleDone ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleDone();
            }}
            class="-ml-1 shrink-0 inline-flex items-center justify-center rounded-full text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            style={{ width: "32px", height: "32px" }}
            aria-label={isDone ? "Aufgabe wieder öffnen" : "Aufgabe als erledigt markieren"}
            aria-pressed={isDone}
          >
            {isDone ? (
              <span
                class="inline-flex items-center justify-center size-4 rounded-sm text-[color:var(--color-on-accent)]"
                style={{ background: TASK_STATUS_VAR.done }}
              >
                <Check size={14} strokeWidth={3} aria-hidden="true" />
              </span>
            ) : (
              <span
                class="inline-block size-3.5 rounded-sm border-2"
                style={{ borderColor: TASK_STATUS_VAR[task.status] }}
              />
            )}
          </button>
        ) : (
          <span
            class="inline-block size-2.5 rounded-sm border shrink-0"
            style={{
              borderColor: TASK_STATUS_VAR[task.status],
              background:
                task.status === "done" ? TASK_STATUS_VAR.done : "transparent",
            }}
            aria-hidden="true"
          />
        )}
        {isBlocked && (
          <AlertCircle
            size={14}
            class="text-[color:var(--color-status-blocked)] shrink-0"
            aria-label="Blockiert"
          />
        )}
        <span
          class={`flex-1 min-w-0 truncate text-sm text-[color:var(--color-on-surface)] ${
            task.status === "done" ? "line-through opacity-70" : ""
          }`}
        >
          {task.title}
        </span>
        {task.attachment_count > 0 && (
          <span
            class="shrink-0 inline-flex items-center gap-0.5 text-label text-[color:var(--color-on-surface-variant)]"
            aria-label={`${task.attachment_count} Anhänge`}
          >
            <Paperclip size={14} aria-hidden="true" />
            {task.attachment_count}
          </span>
        )}
      </div>
      <div class="flex flex-wrap items-center gap-1.5 pl-[18px]">
        <span
          class="inline-flex items-center rounded px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-accent)]"
          style={{ background: TASK_STATUS_VAR[task.status] }}
        >
          {TASK_STATUS_LABEL[task.status]}
        </span>
        {task.priority && (
          <span
            class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-accent)]"
            style={{ background: TASK_PRIORITY_VAR[task.priority] }}
          >
            <Flag size={14} class="shrink-0" fill="currentColor" aria-hidden="true" />
            {TASK_PRIORITY_LABEL[task.priority]}
          </span>
        )}
        {due && (
          <span class="inline-flex items-center gap-1 text-label text-[color:var(--color-on-surface-variant)]">
            <Calendar size={14} aria-hidden="true" />
            {formatDateShort(due)}
          </span>
        )}
        {labels.slice(0, 2).map((l) => (
          <LabelChip key={l.id} name={l.name} color={l.color} compact />
        ))}
        {labels.length > 2 && (
          <span class="text-label text-[color:var(--color-on-surface-variant)]">
            +{labels.length - 2}
          </span>
        )}
        {assignees.length > 0 && (
          <span class="ml-auto inline-flex items-center gap-0.5">
            {assignees.map((u) => (
              <UserAvatar
                key={u.id}
                userId={u.id}
                name={u.name}
                avatarUrl={u.avatar_url}
                size={20}
              />
            ))}
            {extraAssignees > 0 && (
              <span class="inline-flex items-center justify-center size-5 rounded-full bg-[color:var(--color-surface-container)] text-label font-medium text-[color:var(--color-on-surface-variant)] border border-[color:var(--color-surface-container-lowest)]">
                +{extraAssignees}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Monday-style status cell: fills the whole cell with the status color.
 * When the project uses dynamic statuses (statuses prop + task statusInfo),
 * renders custom name/color and lists the project's statuses in the dropdown.
 * Falls back to the legacy fixed enum when statusInfo is absent.
 */
function StatusSelect(props: {
  value: TaskStatus;
  statusInfo?: StatusInfo | null;
  statuses?: Status[];
  onChange: (v: TaskStatus) => void | Promise<void>;
  onChangeId?: (id: number) => void | Promise<void>;
  readOnly?: boolean;
}): preact.JSX.Element {
  const { statusInfo, statuses, readOnly } = props;
  const useDynamic = statusInfo != null && statuses && statuses.length > 0;

  const displayName = useDynamic ? statusInfo.name : TASK_STATUS_LABEL[props.value];
  const displayColor = useDynamic ? statusInfo.color : TASK_STATUS_VAR[props.value];
  const selectValue = useDynamic ? String(statusInfo.id) : props.value;

  return (
    <div
      class={`juuplan-status-pill group/cell relative w-full rounded text-xs font-medium min-h-[28px] ${
        readOnly ? "" : "hover:brightness-110 cursor-pointer"
      }`}
      style={{
        background: displayColor,
        // Dynamic statuses carry a stored hex → pick black/white by luminance;
        // enum statuses use the theme token, where --color-on-accent matches.
        color: useDynamic ? pickFg(statusInfo.color) : "var(--color-on-accent)",
      }}
    >
      {!readOnly && (
        <select
          value={selectValue}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            if (useDynamic && props.onChangeId) {
              void props.onChangeId(Number(v));
            } else {
              void props.onChange(v as TaskStatus);
            }
          }}
          class="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
          aria-label="Status"
        >
          {useDynamic
            ? (statuses ?? []).map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))
            : (["backlog", "in_progress", "review", "done", "blocked"] as TaskStatus[]).map((s) => (
                <option key={s} value={s}>
                  {TASK_STATUS_LABEL[s]}
                </option>
              ))}
        </select>
      )}
      {/* Bedienbarkeits-Hinweis, siehe `PriorityPill` in TaskRow.tsx: der
          Select liegt unsichtbar über der Zelle. */}
      <span class="flex items-center justify-center gap-1 w-full h-full pointer-events-none px-2 py-1 select-none">
        {displayName}
        {!props.readOnly && (
          <ChevronDown
            size={14}
            class="shrink-0 opacity-0 transition-opacity group-hover/cell:opacity-60 peer-focus:opacity-100"
            aria-hidden="true"
          />
        )}
      </span>
    </div>
  );
}

function PrioritySelect(props: {
  value: TaskPriority | null;
  onChange: (v: TaskPriority | null) => void | Promise<void>;
  readOnly?: boolean;
}): preact.JSX.Element {
  const filled = props.value !== null;
  return (
    <div
      class={`group/cell relative w-full rounded text-xs font-medium transition-all min-h-[28px] ${
        props.readOnly ? "" : "cursor-pointer"
      } ${
        filled
          ? `text-[color:var(--color-on-accent)] ${props.readOnly ? "" : "hover:brightness-110"}`
          : `text-[color:var(--color-on-surface-variant)] border border-dashed border-[color:var(--color-outline-variant)] ${props.readOnly ? "" : "hover:bg-[color:var(--color-surface-container-low)]"}`
      }`}
      style={filled ? { background: TASK_PRIORITY_VAR[props.value as TaskPriority] } : undefined}
    >
      {!props.readOnly && (
      <select
        value={props.value ?? ""}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value as TaskPriority | "";
          void props.onChange(v === "" ? null : v);
        }}
        class="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
        aria-label="Priorität"
      >
        <option value="">—</option>
        {(["low", "medium", "high", "urgent"] as TaskPriority[]).map((p) => (
          <option key={p} value={p}>
            {TASK_PRIORITY_LABEL[p]}
          </option>
        ))}
      </select>
      )}
      <span class="flex items-center justify-center gap-1 w-full h-full pointer-events-none px-2 py-1 select-none">
        <Flag
          size={14}
          class="shrink-0"
          fill={filled ? "currentColor" : "none"}
          aria-hidden="true"
        />
        {filled ? TASK_PRIORITY_LABEL[props.value as TaskPriority] : "—"}
        {!props.readOnly && (
          <ChevronDown
            size={14}
            class="shrink-0 opacity-0 transition-opacity group-hover/cell:opacity-60 peer-focus:opacity-100"
            aria-hidden="true"
          />
        )}
      </span>
    </div>
  );
}
